import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Clock, Layers, Search, Code, 
  Layout, FileText, Plus, X, ShieldCheck, 
  Loader2, Edit3, Trash2, MoreVertical, 
  AlertCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from "react-hot-toast";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCourseId, setCurrentCourseId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    category: "Development",
    duration: "",
    fee: 0,
    modules_count: 0,
    description: ""
  });

  const categories = ['All', 'Development', 'Office Skills', 'Design', 'General'];

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (err) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setFormData({ name: "", category: "Development", duration: "", fee: 0, modules_count: 0, description: "" });
    setShowModal(true);
  };

  const handleOpenEdit = (course) => {
    setIsEditing(true);
    setCurrentCourseId(course.id);
    setFormData({
      name: course.name,
      category: course.category,
      duration: course.duration,
      fee: course.course_fee || course.fee,
      modules_count: course.modules_count,
      description: course.description
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submissionData = {
        ...formData,
        fee: parseFloat(formData.fee),
        course_fee: parseFloat(formData.fee),
        modules_count: parseInt(formData.modules_count)
      };

      if (isEditing) {
        const { error } = await supabase
          .from("courses")
          .update(submissionData)
          .eq("id", currentCourseId);
        if (error) throw error;
        toast.success("Course updated successfully!");
      } else {
        const { error } = await supabase.from("courses").insert([submissionData]);
        if (error) throw error;
        toast.success("Course added successfully!");
      }

      setShowModal(false);
      fetchCourses();
    } catch (err) {
      toast.error(err.message || "Error saving course");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this course? This action cannot be undone.")) return;
    
    try {
      const { error } = await supabase.from("courses").delete().eq("id", id);
      if (error) throw error;
      toast.success("Course deleted");
      setCourses(courses.filter(c => c.id !== id));
    } catch (err) {
      toast.error("Failed to delete course");
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesFilter = filter === 'All' || course.category === filter;
    const matchesSearch = course.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getIcon = (cat) => {
    switch (cat) {
      case 'Development': return <Code size={16} className="text-orange-500" />;
      case 'Design': return <Layout size={16} className="text-rose-500" />;
      case 'Office Skills': return <FileText size={16} className="text-amber-500" />;
      default: return <Layers size={16} className="text-emerald-500" />;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-700 p-4">
      
      {/* --- Page Header --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-6 rounded-[2rem] border border-orange-50 shadow-sm">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight italic">Academy <span className="text-orange-500">Curriculum</span></h1>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Manage Course Catalog</p>
        </div>
        <button 
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black shadow-lg hover:bg-orange-500 transition-all active:scale-95"
        >
          <Plus size={18} />
          Add Course
        </button>
      </div>

      {/* --- Search & Filter --- */}
      <div className="flex flex-col lg:flex-row items-center gap-4">
        <div className="p-1 bg-slate-100 rounded-xl flex flex-wrap gap-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                filter === cat ? "bg-white text-orange-500 shadow-sm" : "text-slate-400 hover:text-slate-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        
        <div className="relative flex-1 w-full lg:max-w-md ml-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
          <input 
            type="text"
            placeholder="Quick search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:border-orange-500 transition-all font-bold text-sm shadow-sm"
          />
        </div>
      </div>

      {/* --- Table Format --- */}
      <div className="bg-white rounded-[2rem] border border-orange-50 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-orange-50">
                <th className="px-8 py-4">Course Details</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Duration</th>
                <th className="px-6 py-4">Tuition Fee</th>
                <th className="px-6 py-4">Modules</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-orange-50/50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-20 text-center">
                    <Loader2 className="animate-spin text-orange-500 mx-auto mb-2" size={30} />
                    <p className="text-slate-400 font-bold italic text-sm">Syncing curriculum...</p>
                  </td>
                </tr>
              ) : filteredCourses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-20 text-center text-slate-400 italic">No courses found matching your criteria.</td>
                </tr>
              ) : (
                filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-orange-50/20 transition-all group">
                    <td className="px-8 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-50 rounded-lg">{getIcon(course.category)}</div>
                        <div>
                          <p className="font-black text-slate-800 text-sm leading-tight">{course.name}</p>
                          <p className="text-[10px] text-slate-400 line-clamp-1 max-w-[200px]">{course.description || "N/A"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-orange-50 text-orange-600 text-[9px] font-black uppercase rounded-md tracking-wider">
                        {course.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-600 italic">
                      {course.duration}
                    </td>
                    <td className="px-6 py-4 font-black text-slate-900 text-sm">
                      ₹{(course.course_fee || course.fee).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-slate-500 font-bold text-xs">
                        <BookOpen size={14} className="text-orange-400" /> {course.modules_count}
                      </div>
                    </td>
                    <td className="px-8 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleOpenEdit(course)}
                          className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(course.id)}
                          className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Course Modal (Add/Edit) --- */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black italic tracking-tight text-orange-500">
                  {isEditing ? "Modify Course" : "New Academy Course"}
                </h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                  {isEditing ? "Update existing curriculum" : "Define new curriculum"}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Course Name</label>
                <input 
                  required type="text" value={formData.name}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none focus:border-orange-500 transition-all" 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Category</label>
                  <select 
                    value={formData.category}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none appearance-none" 
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option>Development</option>
                    <option>Office Skills</option>
                    <option>Design</option>
                    <option>General</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Duration</label>
                  <input 
                    required type="text" value={formData.duration} placeholder="e.g. 12 Months"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none" 
                    onChange={(e) => setFormData({...formData, duration: e.target.value})} 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Tuition Fee (₹)</label>
                  <input 
                    required type="number" value={formData.fee}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none" 
                    onChange={(e) => setFormData({...formData, fee: e.target.value})} 
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Total Modules</label>
                  <input 
                    required type="number" value={formData.modules_count}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none" 
                    onChange={(e) => setFormData({...formData, modules_count: e.target.value})} 
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Description</label>
                <textarea 
                  rows="3" value={formData.description}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold text-sm outline-none focus:border-orange-500" 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                ></textarea>
              </div>

              <button 
                type="submit" disabled={isSubmitting}
                className="w-full py-4 bg-orange-500 text-white rounded-2xl font-black text-sm shadow-xl hover:bg-slate-900 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
                {isSubmitting ? "Processing..." : isEditing ? "Save Changes" : "Deploy Course"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}